import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { tagsAPI } from '../../services/api';

// Single async thunk to fetch tags (handles both global limit:1000 and paginated params)
export const fetchTags = createAsyncThunk('tags/fetchTags', async (params, { rejectWithValue }) => {
  try {
    const response = await tagsAPI.getAll(params);
    return response; // { data: [...], pagination: {...} } OR { data: [...] } depending on API response structure
  } catch (error) {
    return rejectWithValue(error.message || 'Failed to fetch tags');
  }
});

export const createTag = createAsyncThunk(
  'tags/createTag',
  async (tagData, { rejectWithValue }) => {
    try {
      const response = await tagsAPI.create(tagData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create tag');
    }
  },
);

export const updateTag = createAsyncThunk(
  'tags/updateTag',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await tagsAPI.update(id, data);
      return response.data || { ...data, id };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update tag');
    }
  },
);

export const deleteTag = createAsyncThunk('tags/deleteTag', async (id, { rejectWithValue }) => {
  try {
    await tagsAPI.delete(id);
    return id;
  } catch (error) {
    return rejectWithValue(error.message || 'Failed to delete tag');
  }
});

export const bulkDeleteTags = createAsyncThunk(
  'tags/bulkDeleteTags',
  async (data, { rejectWithValue }) => {
    try {
      await tagsAPI.bulkDelete(data);
      return data.ids;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete tags');
    }
  },
);

const tagsSlice = createSlice({
  name: 'tags',
  initialState: {
    // Paginated tags for Tags page
    paginatedItems: [],
    paginatedTotal: 0,
    paginatedStatus: 'idle',
    // Global tags for dropdowns (limit: 1000)
    globalItems: [],
    globalStatus: 'idle',
    error: null,
  },
  reducers: {
    clearTags: (state) => {
      state.paginatedItems = [];
      state.paginatedTotal = 0;
      state.paginatedStatus = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Tags
      .addCase(fetchTags.pending, (state, action) => {
        // Check if this is a paginated request (limit <= 100) or global request (limit >= 1000)
        const limit = action.meta.arg?.limit || 12;
        if (limit >= 1000) {
          state.globalStatus = 'loading';
        } else {
          state.paginatedStatus = 'loading';
        }
      })
      .addCase(fetchTags.fulfilled, (state, action) => {
        // Determine if this was a paginated or global request
        const limit = action.meta.arg?.limit || 12;
        const isGlobalRequest = limit >= 1000;

        // Handle response structure depending on whether it's paginated or not
        if (action.payload.data) {
          if (isGlobalRequest) {
            // Store in global items for dropdowns
            state.globalItems = action.payload.data;
            state.globalStatus = 'succeeded';
          } else {
            // Store in paginated items for Tags page
            state.paginatedItems = action.payload.data;
            state.paginatedTotal = action.payload.pagination?.total || action.payload.data.length;
            state.paginatedStatus = 'succeeded';
          }
        } else {
          // Fallback if response is just array
          const items = Array.isArray(action.payload) ? action.payload : [];
          if (isGlobalRequest) {
            state.globalItems = items;
            state.globalStatus = 'succeeded';
          } else {
            state.paginatedItems = items;
            state.paginatedTotal = items.length;
            state.paginatedStatus = 'succeeded';
          }
        }
        state.error = null;
      })
      .addCase(fetchTags.rejected, (state, action) => {
        const limit = action.meta.arg?.limit || 12;
        if (limit >= 1000) {
          state.globalStatus = 'failed';
        } else {
          state.paginatedStatus = 'failed';
        }
        state.error = action.payload;
      })

      // Create Tag
      .addCase(createTag.fulfilled, (state, action) => {
        // Add to both paginated and global items
        state.paginatedItems.push(action.payload);
        state.paginatedTotal += 1;
        state.globalItems.push(action.payload);
      })

      // Update Tag
      .addCase(updateTag.fulfilled, (state, action) => {
        // Update in both paginated and global items
        const paginatedIndex = state.paginatedItems.findIndex(
          (tag) => tag.id === action.payload.id,
        );
        if (paginatedIndex !== -1) {
          state.paginatedItems[paginatedIndex] = action.payload;
        }
        const globalIndex = state.globalItems.findIndex((tag) => tag.id === action.payload.id);
        if (globalIndex !== -1) {
          state.globalItems[globalIndex] = action.payload;
        }
      })

      // Delete Tag
      .addCase(deleteTag.fulfilled, (state, action) => {
        // Remove from both paginated and global items
        state.paginatedItems = state.paginatedItems.filter((tag) => tag.id !== action.payload);
        state.paginatedTotal -= 1;
        state.globalItems = state.globalItems.filter((tag) => tag.id !== action.payload);
      })

      // Bulk Delete Tags
      .addCase(bulkDeleteTags.fulfilled, (state, action) => {
        if (action.payload && Array.isArray(action.payload)) {
          // Remove from both paginated and global items
          state.paginatedItems = state.paginatedItems.filter(
            (tag) => !action.payload.includes(tag.id),
          );
          state.paginatedTotal -= action.payload.length;
          state.globalItems = state.globalItems.filter((tag) => !action.payload.includes(tag.id));
        }
      });
  },
});

export const { clearTags } = tagsSlice.actions;

// Selectors for paginated tags (Tags page)
export const selectPaginatedTags = (state) => state.tags.paginatedItems;
export const selectPaginatedTagsStatus = (state) => state.tags.paginatedStatus;
export const selectPaginatedTagsTotal = (state) => state.tags.paginatedTotal;

// Selectors for global tags (dropdowns)
export const selectGlobalTags = (state) => state.tags.globalItems;
export const selectGlobalTagsStatus = (state) => state.tags.globalStatus;

// Legacy selectors for backward compatibility (use paginated by default)
export const selectAllTags = (state) => state.tags.paginatedItems;
export const selectTagsStatus = (state) => state.tags.paginatedStatus;
export const selectTagsError = (state) => state.tags.error;
export const selectTagsTotal = (state) => state.tags.paginatedTotal;

export default tagsSlice.reducer;
